import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SalesChangesOverTime = ({ data }) => {
  const [salesData, setSalesData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculateMonthlySales = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  useEffect(() => {
    if (data && data.length > 0) {
      const aggregatedData = data.reduce((acc, row) => {
        const contractor = row.NazwaKontrahenta || "Nieznany kontrahent";
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const sales = calculateMonthlySales(row);

        if (!acc[contractor]) acc[contractor] = {};
        if (!acc[contractor][year]) acc[contractor][year] = Array(12).fill(0);
        acc[contractor][year][month - 1] += sales;

        return acc;
      }, {});

      const years = [
        ...new Set(
          Object.values(aggregatedData)
            .flatMap((contractor) => Object.keys(contractor))
            .map((y) => parseInt(y, 10))
        ),
      ].sort((a, b) => b - a);

      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
      setSalesData(aggregatedData);
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear) {
      const contractorsWithSales = Object.entries(salesData).map(([contractor, yearlyData]) => ({
        contractor,
        totalSales: yearlyData[selectedYear]?.reduce((sum, val) => sum + val, 0) || 0,
        monthlySales: yearlyData[selectedYear] || Array(12).fill(0),
      }));

      const topContractors = contractorsWithSales
        .sort((a, b) => b.totalSales - a.totalSales)
        .slice(0, 10);

      const labels = [
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
      ];

      setChartData({
        labels,
        datasets: topContractors.map((contractor, index) => ({
          label: contractor.contractor,
          data: contractor.monthlySales,
          borderColor: `hsl(${(index * 40) % 360}, 70%, 50%)`,
          backgroundColor: `hsla(${(index * 40) % 360}, 70%, 50%, 0.2)`,
          fill: false,
          tension: 0.4,
          pointRadius: 4,
        })),
      });
    }
  }, [selectedYear, salesData]);

  if (!data || data.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="sales-time-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="sales-time-info"
        place="left"
        content={
          <>
            <strong>Zmiany Sprzedaży</strong>
            <br />
            Pokazuje miesięczną sprzedaż dla 10
            <br />
            największych kontrahentów w wybranym roku.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
y: {
  beginAtZero: true,
  title: {
    display: true,
    text: "Sprzedaż",
  },
  ticks: {
    callback: function (value) {
      return Number(value).toLocaleString("pl-PL");
    },
  },
},

              x: {
                title: {
                  display: true,
                  text: "Miesiąc",
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Sprzedaż top 10 kontrahentów wg miesięcy ${selectedYear}`,
              },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `Sprzedaż: ${context.raw.toLocaleString("pl-PL")}`,
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  boxWidth: 20,
                },
              },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default SalesChangesOverTime;
